import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { stripHtml } from 'src/app/common/util/html-utils';
import { TableColumnsService } from '../../../service/table-columns.service';
import {
  isSurfaceLike,
  toMinimapColor,
  resolveColorFromVarChain,
  resolveGradientColor
} from './minimap-color-utils';
import { MinimapContext } from './prime-table.context';

const MINIMAP_HEIGHT_MIN = 40;
const MINIMAP_ZOOM_SCALE = 0.70;
const ZOOM_LENS_MIN_W = 160;
const ZOOM_LENS_MIN_H = 100;

/**
 * Manages the minimap canvas (horizontal overview bar) including the zoom lens.
 * All rendering and DOM-event listeners are kept outside Angular zone to
 * avoid triggering change detection on every scroll / pointermove.
 *
 * Applied on `.table-minimap-container` (`[appMinimap]`) — the canvas and viewport
 * elements are static children of that host, so they're looked up once in ngOnInit;
 * no more manual setup()/destroy() dance driven by setTimeout. The zoom lens / zoom
 * canvas live in a structurally separate part of the template (behind their own
 * *ngIf) — the component still feeds those in explicitly via setZoomLensEl()/
 * setZoomCanvas(), called through the exported directive instance (#minimapRef="appMinimap").
 */
@Directive({
  selector: '[appMinimap]',
  exportAs: 'appMinimap'
})
export class MinimapDirective implements OnInit, OnDestroy {

  @Input('appMinimap') ctx!: MinimapContext;

  // ── Listeners / observers ─────────────────────────────────────────────────
  private minimapScrollListener: (() => void) | null = null;
  private minimapCanvasPointerMoveListener: ((e: PointerEvent) => void) | null = null;
  private minimapMutationObserver: MutationObserver | null = null;
  private minimapResizeObserver: ResizeObserver | null = null;

  // ── Render state ──────────────────────────────────────────────────────────
  private minimapRenderScheduled = false;
  minimapNeedsFullScan = true; // set directly by the component when data changes

  // ── Offscreen canvas / scroll state ───────────────────────────────────────
  private minimapOffscreenCanvas: HTMLCanvasElement | null = null;
  private minimapPanOffset = 0;
  private minimapLastScrollTop = -1;
  private minimapScale = 1;
  private minimapContentWidth = 0;
  private minimapDisplayWidth = 0;

  /**
   * When true the height is derived automatically by fitToWidth() on first render.
   * Reset to false after first auto-fit or after any manual resize.
   */
  private minimapAutoHeight = true;

  // ── Drag / resize state (mirrored locally for outside-zone access) ────────
  private minimapDragActive = false;
  private minimapResizeActive = false;

  // ── Resize drag ───────────────────────────────────────────────────────────
  private minimapResizeStartY = 0;
  private minimapResizeStartHeight = 0;
  private minimapResizeMaxHeight = 400;

  // ── Zoom lens ─────────────────────────────────────────────────────────────
  private minimapZoomDocListener: ((e: MouseEvent) => void) | null = null;
  private minimapZoomKeyListener: ((e: KeyboardEvent) => void) | null = null;
  private minimapZoomTableHoverListener: (() => void) | null = null;
  private minimapZoomRenderScheduled = false;
  private minimapZoomPendingCanvasX = 0;
  private minimapZoomPendingCanvasY = 0;
  private minimapZoomIndicatorRect: { x1: number; y1: number; x2: number; y2: number } | null = null;

  // Elements — canvas/viewport resolved from the host in ngOnInit; zoomLens/zoomCanvas/
  // crosshairCanvas fed in explicitly by the component (see class doc above).
  private canvas: HTMLCanvasElement | null;
  private viewportEl: HTMLDivElement | null;
  private zoomLensEl: HTMLDivElement | null = null;
  private zoomCanvas: HTMLCanvasElement | null = null;
  private crosshairCanvas: HTMLCanvasElement | null = null;
  private destroyed = false;

  constructor(
    private hostEl: ElementRef<HTMLElement>,
    private zone: NgZone,
    private columnsService: TableColumnsService,
  ) {
    // NOTE: children of the host element (canvas, viewport div) are NOT queried here.
    // Directive constructors run while Angular is still creating the view — the host
    // element exists, but its own template children haven't been created/appended yet.
    // They're looked up lazily in trySetup() instead (which runs from ngOnInit, by
    // which point the whole embedded view — including these static children — exists).
    this.canvas = null;
    this.viewportEl = null;
  }

  // ── Angular lifecycle ──────────────────────────────────────────────────────

  ngOnInit(): void {
    this.trySetup();
  }

  /**
   * getScrollContainer() depends on the parent's `table` ViewChild, which may not
   * be resolved yet on the very first change-detection tick. Retries for a bounded
   * number of frames instead of relying on the caller to re-trigger setup from
   * several external call sites (the old setTimeout dance).
   */
  private trySetup(attemptsLeft = 20): void {
    if (this.destroyed) return;
    if (!this.canvas) {
      const host = this.hostEl.nativeElement;
      this.canvas = host.querySelector('canvas.table-minimap-canvas');
      this.viewportEl = host.querySelector('.table-minimap-viewport');
    }
    const canvas = this.canvas;
    if (!canvas) {
      if (attemptsLeft <= 0) return;
      requestAnimationFrame(() => this.trySetup(attemptsLeft - 1));
      return;
    }
    const sc = this.ctx.getScrollContainer();
    if (!sc) {
      if (attemptsLeft <= 0) return;
      requestAnimationFrame(() => this.trySetup(attemptsLeft - 1));
      return;
    }

    // Restore saved height for this table
    const dataSourceName = this.ctx.getDataSourceName();
    const savedH = dataSourceName
      ? this.columnsService.getMinimapHeight(dataSourceName)
      : null;
    if (savedH !== null) {
      this.ctx.setMinimapHeight(savedH);
      this.minimapAutoHeight = false;
    } else {
      this.minimapAutoHeight = true;
    }

    this.zone.runOutsideAngular(() => {
      this.minimapScrollListener = () => this.scheduleRender();
      sc.addEventListener('scroll', this.minimapScrollListener, { passive: true });

      this.minimapCanvasPointerMoveListener = (e: PointerEvent) => {
        if (this.minimapDragActive) this.scrollToMinimapPosition(e);
        if (this.showZoomOpen) this.trackZoomLens(e);
      };
      canvas.addEventListener('pointermove', this.minimapCanvasPointerMoveListener, { passive: true });

      this.minimapMutationObserver = new MutationObserver((mutations) => {
        if (mutations.some(m => m.type === 'attributes')) {
          this.minimapNeedsFullScan = true;
        }
        this.scheduleRender();
      });
      this.minimapMutationObserver.observe(sc, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });

      this.minimapResizeObserver = new ResizeObserver(() => {
        this.minimapNeedsFullScan = true;
        this.scheduleRender();
      });
      this.minimapResizeObserver.observe(sc);
    });

    this.scheduleRender();
    this.updateIndicator(sc);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closeZoom();

    if (this.minimapScrollListener) {
      const sc = this.ctx.getScrollContainer();
      sc?.removeEventListener('scroll', this.minimapScrollListener);
      this.minimapScrollListener = null;
    }
    if (this.minimapCanvasPointerMoveListener && this.canvas) {
      this.canvas.removeEventListener('pointermove', this.minimapCanvasPointerMoveListener);
      this.minimapCanvasPointerMoveListener = null;
    }
    this.minimapMutationObserver?.disconnect();
    this.minimapMutationObserver = null;
    this.minimapResizeObserver?.disconnect();
    this.minimapResizeObserver = null;

    this.minimapOffscreenCanvas = null;
    this.minimapLastScrollTop = -1;
    this.minimapNeedsFullScan = true;
    this.zoomLensEl = null;
    this.zoomCanvas = null;
    this.crosshairCanvas = null;
  }

  // ── Public: called from component template / methods ──────────────────────

  /**
   * Fits the minimap height so the full table width fits without horizontal panning.
   * Uses the same analytical formula as the original component.
   */
  fitToWidth(): void {
    const canvas = this.canvas;
    const sc = this.ctx.getScrollContainer();
    if (!canvas || !sc) return;

    const canvasW = canvas.offsetWidth;
    if (canvasW === 0) return;

    const tableHost = this.ctx.getTableHost();
    const headerRow = tableHost?.querySelector('thead tr:last-child');
    const ths = headerRow ? Array.from(headerRow.querySelectorAll('th')) : [];
    const tableW = ths.reduce((s, th) => s + (th as HTMLElement).offsetWidth, 0);
    if (tableW <= canvasW) return;

    const rowH = this.ctx.getNavRowHeight();
    if (rowH <= 0) return;

    const minimapHeight = this.ctx.getMinimapHeight();
    const totalH = sc.clientHeight + minimapHeight;
    const n = Math.ceil((totalH + rowH) * tableW / (rowH * (tableW + canvasW)));
    if (n <= 0) return;

    const targetH = Math.floor(n * rowH * canvasW / tableW);
    const clampedH = Math.max(
      MINIMAP_HEIGHT_MIN,
      Math.min(targetH, totalH - MINIMAP_HEIGHT_MIN * 2)
    );

    if (clampedH === minimapHeight) return;

    this.minimapAutoHeight = false;
    this.minimapNeedsFullScan = true;
    this.zone.run(() => {
      this.ctx.setMinimapHeight(clampedH);
      this.ctx.scheduleTableHeightRecalc();
    });
    const dsName = this.ctx.getDataSourceName();
    if (dsName) {
      this.columnsService.saveMinimapHeight(dsName, clampedH);
    }
    this.scheduleRender();
  }

  onPointerDown(event: PointerEvent): void {
    if (event.button === 1) {
      event.preventDefault();
      this.toggleZoom(event);
      return;
    }
    if (event.button !== 0) return;
    this.minimapDragActive = true;
    this.ctx.setMinimapDragging(true);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.scrollToMinimapPosition(event);
  }

  onPointerUp(): void {
    this.minimapDragActive = false;
    this.ctx.setMinimapDragging(false);
  }

  onResizeStart(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.minimapResizeActive = true;
    this.ctx.setMinimapResizing(true);
    this.minimapResizeStartY = event.clientY;
    this.minimapResizeStartHeight = this.ctx.getMinimapHeight();
    this.minimapAutoHeight = false;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);

    const vp = this.ctx.getScrollContainer();
    const canvas = this.canvas;
    const canvasW = canvas?.offsetWidth ?? 800;
    const tableClientW = Math.max(1, vp?.clientWidth ?? canvasW);
    const containerH = (vp?.clientHeight ?? 400) + this.ctx.getMinimapHeight();
    this.minimapResizeMaxHeight = Math.max(
      MINIMAP_HEIGHT_MIN * 2,
      Math.floor(canvasW * containerH / (2 * tableClientW + canvasW))
    );
  }

  onResizeMove(event: PointerEvent): void {
    if (!this.minimapResizeActive) return;
    const delta = this.minimapResizeStartY - event.clientY;
    const newHeight = Math.max(
      MINIMAP_HEIGHT_MIN,
      Math.min(this.minimapResizeMaxHeight, this.minimapResizeStartHeight + delta)
    );
    if (newHeight === this.ctx.getMinimapHeight()) return;
    this.ctx.setMinimapHeight(newHeight);
    this.ctx.scheduleTableHeightRecalc();
    this.scheduleRender();
  }

  onResizeEnd(): void {
    this.minimapResizeActive = false;
    this.ctx.setMinimapResizing(false);
    const dsName = this.ctx.getDataSourceName();
    if (dsName) {
      this.columnsService.saveMinimapHeight(dsName, this.ctx.getMinimapHeight());
    }
  }

  toggleZoom(event: PointerEvent): void {
    if (this.showZoomOpen) {
      this.closeZoom();
      return;
    }
    const canvas = this.canvas;
    if (!canvas || !this.minimapOffscreenCanvas) return;

    const sc = this.ctx.getScrollContainer();
    const realRowH = this.ctx.getNavRowHeight();
    const clientH = sc?.clientHeight ?? 400;
    const maxBodySlots = Math.max(1, Math.floor(clientH / realRowH));
    const rowsInPanel = Math.min(maxBodySlots + 1, Math.floor(300 / realRowH));
    const WINDOW_W = Math.min(520, window.innerWidth - 16);
    const WINDOW_H = rowsInPanel * realRowH;

    const rect = canvas.getBoundingClientRect();
    const canvasX = event.clientX - rect.left;
    const canvasY = event.clientY - rect.top;

    let left = Math.round(event.clientX - WINDOW_W / 2);
    let top = Math.round(rect.top - WINDOW_H - 6);
    left = Math.max(8, Math.min(window.innerWidth - WINDOW_W - 8, left));
    top = top < 8 ? Math.round(rect.bottom + 6) : top;

    this.zoomW = WINDOW_W;
    this.zoomH = WINDOW_H;
    this.zoomCurrentLeft = left;
    this.zoomCurrentTop = top;
    this.zoomManuallyPositioned = false;
    this.zoomPinned = false;
    this.ctx.setZoomGeometry(left, top, WINDOW_W, WINDOW_H);
    this.ctx.setShowZoom(true);
    this.showZoomOpen = true;

    this.minimapZoomDocListener = (e: MouseEvent) => {
      // Przypięte — ignoruj kliknięcia poza oknem (np. przeciąganie suwaka tabeli)
      if (this.zoomPinned) return;
      // Kliknięcie wewnątrz okna zoom (np. drag handle) — nie zamykaj
      const lensEl = this.zoomLensEl;
      if (lensEl && lensEl.contains(e.target as Node)) return;
      this.closeZoom();
    };
    this.minimapZoomKeyListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.closeZoom();
    };

    setTimeout(() => {
      const lensEl = this.zoomLensEl;
      if (lensEl) {
        lensEl.style.left = `${left}px`;
        lensEl.style.top = `${top}px`;
      }
      this.renderZoom(canvasX, canvasY);
      document.addEventListener('mousedown', this.minimapZoomDocListener);
      document.addEventListener('keydown', this.minimapZoomKeyListener);

      const scrollContainer = this.ctx.getScrollContainer();
      if (scrollContainer) {
        this.minimapZoomTableHoverListener = () =>
          this.scheduleZoomRender(this.minimapZoomPendingCanvasX, this.minimapZoomPendingCanvasY);
        this.zone.runOutsideAngular(() =>
          scrollContainer.addEventListener('mousemove', this.minimapZoomTableHoverListener)
        );
      }
    });
  }

  closeZoom(): void {
    this.showZoomOpen = false;
    this.zoomManuallyPositioned = false;
    this.zoomPinned = false;
    this.ctx.setShowZoom(false);
    if (this.minimapZoomDocListener) {
      document.removeEventListener('mousedown', this.minimapZoomDocListener);
      this.minimapZoomDocListener = null;
    }
    if (this.minimapZoomKeyListener) {
      document.removeEventListener('keydown', this.minimapZoomKeyListener);
      this.minimapZoomKeyListener = null;
    }
    if (this.minimapZoomTableHoverListener) {
      const sc = this.ctx.getScrollContainer();
      sc?.removeEventListener('mousemove', this.minimapZoomTableHoverListener);
      this.minimapZoomTableHoverListener = null;
    }
    this.minimapZoomIndicatorRect = null;
    this.scheduleRender();

    const crosshairCanvas = this.crosshairCanvas;
    if (crosshairCanvas) {
      crosshairCanvas.style.display = 'none';
      const crosshairCtx = crosshairCanvas.getContext('2d');
      crosshairCtx?.clearRect(0, 0, crosshairCanvas.width, crosshairCanvas.height);
    }
  }

  /**
   * Rozpoczyna przeciąganie okna zoom (wywoływane z (pointerdown) na drag handle).
   * Po pierwszym przeciągnięciu okno "odpina się" od kursora — śledzenie poziome
   * przestaje przesuwać okno, ale zawartość nadal aktualizuje się przy ruchu myszy.
   */
  startZoomDrag(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.zoomManuallyPositioned = true;

    const lensEl = this.zoomLensEl;
    if (!lensEl) return;

    const startMouseX = event.clientX;
    const startMouseY = event.clientY;
    const startLeft = this.zoomCurrentLeft;
    const startTop = this.zoomCurrentTop;

    // Blokuj zaznaczanie tekstu i ustaw kursor grabbing na czas dragu
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';

    // Wyłącz hover (i wszelkie inne zdarzenia myszy) na tabeli głównej —
    // bez tego kursor przejeżdżający nad wierszami podczas dragu wyzwala
    // ich :hover, co niepotrzebnie zmienia tło wierszy pod przesuwanym oknem.
    const tableHost = this.ctx.getTableHost();
    if (tableHost) tableHost.style.pointerEvents = 'none';

    const handleMove = (e: PointerEvent): void => {
      const newLeft = Math.max(8, Math.min(window.innerWidth - this.zoomW - 8, startLeft + e.clientX - startMouseX));
      const newTop = Math.max(8, Math.min(window.innerHeight - this.zoomH - 8, startTop + e.clientY - startMouseY));
      this.zoomCurrentLeft = newLeft;
      this.zoomCurrentTop = newTop;
      lensEl.style.left = `${newLeft}px`;
      lensEl.style.top = `${newTop}px`;
      // Synchronizacja Angular bindings — zapobiega resetowi pozycji przy CD
      this.ctx.setZoomGeometry(newLeft, newTop, this.zoomW, this.zoomH);
    };

    const handleUp = (): void => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (tableHost) tableHost.style.pointerEvents = '';
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleUp);
    };

    // Słuchacze na document (nie na elemencie) — omijamy pointer-events: none
    // na rodzicu .minimap-zoom-lens; uruchamiamy poza strefą Angular, żeby
    // pointermove nie wyzwalał detekcji zmian przy każdym ruchu myszki.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointermove', handleMove);
      document.addEventListener('pointerup', handleUp);
      document.addEventListener('pointercancel', handleUp);
    });
  }

  /**
   * Zmiana rozmiaru okna zoom przez uchwyt w prawym dolnym rogu — dostępna
   * tylko gdy okno jest przypięte (tak samo jak swobodne przeciąganie).
   * Maksymalny rozmiar to połowa szerokości/wysokości viewportu; okno nie
   * może też wyjechać poza prawą/dolną krawędź ekranu przy powiększaniu.
   */
  startZoomResize(event: PointerEvent): void {
    if (!this.zoomPinned) return;
    event.preventDefault();
    event.stopPropagation();

    const lensEl = this.zoomLensEl;
    if (!lensEl) return;

    const startMouseX = event.clientX;
    const startMouseY = event.clientY;
    const startW = this.zoomW;
    const startH = this.zoomH;
    const left = this.zoomCurrentLeft;
    const top = this.zoomCurrentTop;

    const maxW = Math.max(ZOOM_LENS_MIN_W, Math.min(window.innerWidth / 2, window.innerWidth - left - 8));
    const maxH = Math.max(ZOOM_LENS_MIN_H, Math.min(window.innerHeight / 2, window.innerHeight - top - 8));

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'nwse-resize';

    const tableHost = this.ctx.getTableHost();
    if (tableHost) tableHost.style.pointerEvents = 'none';

    const handleMove = (e: PointerEvent): void => {
      const newW = Math.max(ZOOM_LENS_MIN_W, Math.min(maxW, startW + (e.clientX - startMouseX)));
      const newH = Math.max(ZOOM_LENS_MIN_H, Math.min(maxH, startH + (e.clientY - startMouseY)));
      this.zoomW = newW;
      this.zoomH = newH;
      lensEl.style.width = `${newW}px`;
      lensEl.style.height = `${newH}px`;
      // Synchronizacja Angular bindings — zapobiega resetowi rozmiaru przy CD
      this.ctx.setZoomGeometry(left, top, newW, newH);
      // Zawartość canvasu zależy od W/H (patrz renderZoom) — trzeba przerysować
      this.renderZoom(this.minimapZoomPendingCanvasX, this.minimapZoomPendingCanvasY);
    };

    const handleUp = (): void => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (tableHost) tableHost.style.pointerEvents = '';
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleUp);
    };

    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointermove', handleMove);
      document.addEventListener('pointerup', handleUp);
      document.addEventListener('pointercancel', handleUp);
    });
  }

  /**
   * Przełącza przypięcie okna zoom (przycisk pinezki w rogu okna).
   * Przypięte okno nie zamyka się samo na klik poza nim (np. przy
   * przeciąganiu suwaka tabeli) — celowo pełni też rolę przycisku zamknięcia:
   * ponowne kliknięcie pinezki odpina i zamyka okno (bez osobnego krzyżyka).
   */
  toggleZoomPin(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.zoomPinned) {
      this.closeZoom();
    } else {
      this.zoomPinned = true;
    }
  }

  // ── Public helper (called by component when data changes) ─────────────────

  scheduleRender(): void {
    if (this.minimapRenderScheduled) return;
    this.minimapRenderScheduled = true;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.minimapRenderScheduled = false;
        this.render();
        if (this.showZoomOpen) {
          this.scheduleZoomRender(this.minimapZoomPendingCanvasX, this.minimapZoomPendingCanvasY);
        }
      });
    });
  }

  // Zoom-open state: component sets this via ctx.setShowZoom; we track it locally
  // to avoid cross-zone reads in rAF callbacks.
  private showZoomOpen = false;

  // ── Zoom position tracking ────────────────────────────────────────────────
  /** Aktualnie ustawiona pozycja okna zoom (synchronizowana z ctx.setZoomGeometry). */
  private zoomCurrentLeft = 0;
  private zoomCurrentTop = 0;
  /**
   * true gdy użytkownik ręcznie przesunął okno zoom (drag handle).
   * W tym trybie trackZoomLens aktualizuje tylko zawartość, nie pozycję okna.
   * Odczytywane przez komponent (getter minimapZoomDragged).
   */
  zoomManuallyPositioned = false;

  /**
   * true gdy okno zoom jest przypięte (przycisk pinezki).
   * W tym trybie okno NIE zamyka się automatycznie na klik poza nim
   * (np. przy przeciąganiu suwaka tabeli) — zamyka je dopiero ponowne
   * kliknięcie pinezki (patrz toggleZoomPin). Odczytywane przez
   * komponent (getter minimapZoomPinned).
   */
  zoomPinned = false;

  // ── Private: render ───────────────────────────────────────────────────────

  private render(): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const sc = this.ctx.getScrollContainer();
    if (!sc) return;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;

    const displayWidth = canvas.offsetWidth;
    if (displayWidth === 0) return;

    const currentScrollTop = sc.scrollTop;
    const needScan = this.minimapNeedsFullScan ||
                     currentScrollTop !== this.minimapLastScrollTop ||
                     !this.minimapOffscreenCanvas;

    if (needScan) {
      const tableHost = this.ctx.getTableHost();
      if (!tableHost) return;

      const headerRow = tableHost.querySelector('thead tr:last-child');
      const allBodyRows = Array.from(tableHost.querySelectorAll('tbody tr')) ;
      const expectedCellCount = this.ctx.getDisplayedColumns().length;

      const singleRowHeight = this.ctx.getNavRowHeight();
      const maxBodySlots = Math.max(1, Math.ceil(sc.clientHeight / singleRowHeight));
      const totalSlots = maxBodySlots + (headerRow ? 1 : 0);

      const vpRect = sc.getBoundingClientRect();
      const visibleBodyRows: (Element | null)[] = [];
      for (const row of allBodyRows) {
        if (row.querySelectorAll('td, th').length !== expectedCellCount) continue;
        const r = row.getBoundingClientRect();
        if (r.bottom > vpRect.top && r.top < vpRect.bottom) visibleBodyRows.push(row);
      }
      while (visibleBodyRows.length < maxBodySlots) visibleBodyRows.push(null);

      const rows: (Element | null)[] = [];
      if (headerRow) rows.push(headerRow);
      rows.push(...visibleBodyRows.slice(0, maxBodySlots));

      const colOffsets: number[] = [];
      const colWidths: number[] = [];
      if (headerRow) {
        const ths = Array.from(headerRow.querySelectorAll('th'));
        let cum = 0;
        for (const th of ths) {
          colOffsets.push(cum);
          const w = (th as HTMLElement).offsetWidth || this.ctx.getInitialColumnWidth('');
          colWidths.push(w);
          cum += w;
        }
      } else {
        let cum = 0;
        for (const col of this.ctx.getDisplayedColumns()) {
          colOffsets.push(cum);
          const w = this.ctx.getInitialColumnWidth(col.name);
          colWidths.push(w);
          cum += w;
        }
      }

      const realRowHeight = this.ctx.getNavRowHeight();
      const totalTableWidth = colWidths.reduce((s, w) => s + w, 0);

      if (this.minimapAutoHeight) {
        this.minimapAutoHeight = false;
        const prevH = this.ctx.getMinimapHeight();
        this.fitToWidth();
        if (this.ctx.getMinimapHeight() !== prevH) return;
      }

      const displayHeight = this.ctx.getMinimapHeight();
      if (displayHeight === 0) return;

      const rowHeight = displayHeight / totalSlots;
      const scale = rowHeight / realRowHeight;
      const minimapContentWidth = Math.max(displayWidth, totalTableWidth * scale);
      const offW = Math.ceil(minimapContentWidth);

      if (!this.minimapOffscreenCanvas) this.minimapOffscreenCanvas = document.createElement('canvas');
      this.minimapOffscreenCanvas.width = offW;
      this.minimapOffscreenCanvas.height = displayHeight;
      const offCtx = this.minimapOffscreenCanvas.getContext('2d');
      if (!offCtx) return;

      interface BadgeData { x: number; y: number; w: number; h: number; bg: string; text: string }
      interface CellData {
        x: number; y: number; w: number; h: number;
        bg: string; text: string; textColor: string; fontFamily: string;
        cellFontSize: number; fontWeight: string; hasSvgIcon: boolean;
        badges: BadgeData[];
      }
      const items: CellData[] = [];

      rows.forEach((row, rowIdx) => {
        if (!row) return;
        const rowStyle = getComputedStyle(row);
        const rowBg = this.resolveElementColor(rowStyle, row);
        const y = Math.floor(rowIdx * rowHeight);
        const h = Math.ceil(rowHeight);
        const rowRect = (row as HTMLElement).getBoundingClientRect();
        const cells = row.querySelectorAll('td, th');

        cells.forEach((cell, colIdx) => {
          const cellX = (colOffsets[colIdx] ?? 0) * scale;
          const cellW = (colWidths[colIdx] ?? 0) * scale;
          const cellRect = (cell as HTMLElement).getBoundingClientRect();
          const cellStyle = getComputedStyle(cell);
          let bg = this.resolveCellColor(cellStyle, cell);
          if (!bg) bg = rowBg;

          const violationEls = cell.querySelectorAll('.violation');
          const badges: BadgeData[] = [];
          violationEls.forEach(v => {
            const vRect = (v as HTMLElement).getBoundingClientRect();
            if (vRect.width === 0 || vRect.height === 0) return;
            const vBg = getComputedStyle(v).backgroundColor;
            if (!vBg || vBg === 'rgba(0, 0, 0, 0)' || vBg === 'transparent') return;
            const noCopyEl = (v.querySelector('.no-copy') ?? v) as HTMLElement;
            const symbol = stripHtml(noCopyEl.innerHTML?.trim() ?? noCopyEl.textContent?.trim() ?? '');
            const bX = cellX + (vRect.left - cellRect.left) * scale;
            const bY = y + (vRect.top - rowRect.top) * scale;
            badges.push({
              x: Math.floor(bX), y: Math.floor(bY),
              w: Math.max(2, Math.ceil(vRect.width * scale)),
              h: Math.max(2, Math.ceil(vRect.height * scale)),
              bg: vBg, text: symbol
            });
          });

          const text = (cell.textContent ?? '').trim();
          const hasSvgIcon = badges.length === 0 && !text && !!cell.querySelector('svg');
          items.push({
            x: Math.floor(cellX), y, w: Math.max(1, Math.ceil(cellW)), h, bg,
            text: badges.length > 0 ? '' : text,
            textColor: cellStyle.color, fontFamily: cellStyle.fontFamily,
            cellFontSize: parseFloat(cellStyle.fontSize) || 14,
            fontWeight: cellStyle.fontWeight || 'normal', hasSvgIcon, badges
          });
        });
      });

      offCtx.fillStyle = getComputedStyle(document.body).getPropertyValue('--surface-ground') || '#f8f9fa';
      offCtx.fillRect(0, 0, offW, displayHeight);

      for (const { x, y, w, h, bg } of items) {
        if (bg) { offCtx.fillStyle = bg; offCtx.fillRect(x, y, w, h); }
      }

      offCtx.textBaseline = 'middle';
      for (const { x, y, w, h, text, textColor, fontFamily, cellFontSize, fontWeight, hasSvgIcon, badges } of items) {
        if (w < 4) continue;
        if (badges.length > 0) {
          for (const badge of badges) {
            offCtx.fillStyle = badge.bg;
            offCtx.fillRect(badge.x, badge.y, badge.w, badge.h);
            if (badge.text && badge.w >= 4 && badge.h >= 3) {
              let bFontSize = Math.max(2, Math.min(badge.h - 1, badge.w * 0.75));
              offCtx.font = `bold ${bFontSize}px sans-serif`;
              const measuredW = offCtx.measureText(badge.text).width;
              const availW = badge.w - 2;
              if (measuredW > availW && availW > 0) {
                bFontSize = Math.max(2, bFontSize * availW / measuredW);
                offCtx.font = `bold ${bFontSize}px sans-serif`;
              }
              offCtx.fillStyle = 'rgba(255,255,255,0.9)';
              offCtx.save();
              offCtx.beginPath(); offCtx.rect(badge.x, badge.y, badge.w, badge.h); offCtx.clip();
              offCtx.fillText(badge.text, badge.x + 1, badge.y + badge.h / 2);
              offCtx.restore();
            }
          }
          continue;
        }
        if (hasSvgIcon) {
          offCtx.fillStyle = textColor || 'rgba(0,0,0,0.45)';
          const s = Math.max(1, Math.min(w * 0.5, h * 0.5));
          offCtx.fillRect(x + (w - s) / 2, y + (h - s) / 2, s, s);
          continue;
        }
        if (!text) continue;
        const fontSize = Math.max(2, Math.min(cellFontSize * scale, h - 1));
        offCtx.font = `${fontWeight || 'normal'} ${fontSize}px ${fontFamily || 'sans-serif'}`;
        offCtx.fillStyle = textColor || 'rgba(0,0,0,0.8)';
        offCtx.save();
        offCtx.beginPath(); offCtx.rect(x + 1, y, w - 1, h); offCtx.clip();
        offCtx.fillText(text, x + 2, y + h / 2);
        offCtx.restore();
      }

      offCtx.fillStyle = 'rgba(0, 0, 0, 0.06)';
      for (let i = 1; i < rows.length; i++)
        offCtx.fillRect(0, Math.floor(i * rowHeight), offW, 1);

      offCtx.fillStyle = 'rgba(0, 0, 0, 0.04)';
      for (let i = 1; i < colOffsets.length; i++) {
        const x = Math.floor(colOffsets[i] * scale);
        if (x > 0 && x < offW) offCtx.fillRect(x, 0, 1, displayHeight);
      }

      this.minimapScale = scale;
      this.minimapContentWidth = minimapContentWidth;
      this.minimapDisplayWidth = displayWidth;
      this.minimapLastScrollTop = currentScrollTop;
      this.minimapNeedsFullScan = false;
    }

    const displayHeight = this.ctx.getMinimapHeight();
    if (displayHeight === 0 || !this.minimapOffscreenCanvas) return;

    if (canvas.width !== displayWidth) canvas.width = displayWidth;
    if (canvas.height !== displayHeight) canvas.height = displayHeight;

    const tableMaxScroll = sc.scrollWidth - sc.clientWidth;
    const maxPanOffset = Math.max(0, this.minimapContentWidth - displayWidth);
    const panOffset = tableMaxScroll > 0 ? (sc.scrollLeft / tableMaxScroll) * maxPanOffset : 0;
    this.minimapPanOffset = panOffset;

    const sx = Math.round(panOffset);
    const sw = Math.min(displayWidth, this.minimapOffscreenCanvas.width - sx);
    if (sw > 0) {
      ctx2d.drawImage(this.minimapOffscreenCanvas, sx, 0, sw, displayHeight, 0, 0, sw, displayHeight);
      if (sw < displayWidth) {
        ctx2d.fillStyle = getComputedStyle(document.body).getPropertyValue('--surface-ground') || '#f8f9fa';
        ctx2d.fillRect(sw, 0, displayWidth - sw, displayHeight);
      }
    }

    this.updateIndicator(sc);
    this.drawZoomIndicator();
  }

  // ── Private: indicator ────────────────────────────────────────────────────

  private updateIndicator(sc: HTMLElement): void {
    const { scrollLeft, clientWidth } = sc;
    const left = Math.floor(scrollLeft * this.minimapScale - this.minimapPanOffset);
    const width = Math.max(4, Math.ceil(clientWidth * this.minimapScale));
    this.ctx.setMinimapIndicator(left, width);
    const el = this.viewportEl;
    if (el) {
      el.style.transform = `translateX(${left}px)`;
      el.style.width = `${width}px`;
    }
  }

  // ── Private: zoom indicator ───────────────────────────────────────────────

  private drawZoomIndicator(): void {
    const r = this.minimapZoomIndicatorRect;
    if (!r) return;
    const canvas = this.canvas;
    if (!canvas) return;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;

    const cW = canvas.width;
    const cH = canvas.height;
    const x = Math.max(0, Math.round(r.x1));
    const y = Math.max(0, Math.round(r.y1));
    const x2 = Math.min(cW, Math.round(r.x2));
    const y2 = Math.min(cH, Math.round(r.y2));
    const w = x2 - x;
    const h = y2 - y;
    if (w <= 0 || h <= 0) return;

    ctx2d.save();
    ctx2d.fillStyle = 'rgba(80,150,255,0.13)';
    ctx2d.fillRect(x, y, w, h);
    ctx2d.strokeStyle = 'rgba(60,130,255,0.75)';
    ctx2d.lineWidth = 1;
    ctx2d.setLineDash([]);
    ctx2d.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    ctx2d.restore();
  }

  private refreshMinimapWithIndicator(): void {
    const canvas = this.canvas;
    const sc = this.ctx.getScrollContainer();
    if (!canvas || !sc || !this.minimapOffscreenCanvas) return;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;

    const W = canvas.width;
    const H = canvas.height;
    if (W === 0 || H === 0) return;

    const sx = Math.round(this.minimapPanOffset);
    const sw = Math.min(W, this.minimapOffscreenCanvas.width - sx);
    if (sw > 0) {
      ctx2d.drawImage(this.minimapOffscreenCanvas, sx, 0, sw, H, 0, 0, sw, H);
      if (sw < W) {
        ctx2d.fillStyle = getComputedStyle(document.body).getPropertyValue('--surface-ground') || '#f8f9fa';
        ctx2d.fillRect(sw, 0, W - sw, H);
      }
    }
    this.updateIndicator(sc);
    this.drawZoomIndicator();
  }

  // ── Private: scroll position ──────────────────────────────────────────────

  private scrollToMinimapPosition(event: PointerEvent): void {
    const sc = this.ctx.getScrollContainer();
    if (!sc) return;
    const canvasX = event.offsetX;
    const tableX = (canvasX + this.minimapPanOffset) / (this.minimapScale || 1);
    const targetLeft = tableX - sc.clientWidth / 2;
    sc.scrollLeft = Math.max(0, Math.min(targetLeft, sc.scrollWidth - sc.clientWidth));
  }

  // ── Private: zoom lens tracking ───────────────────────────────────────────

  private trackZoomLens(event: PointerEvent): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const canvasX = event.offsetX;
    const canvasY = event.offsetY;

    // Zawartość okna zoom zawsze śledzi kursor (niezależnie od trybu)
    this.scheduleZoomRender(canvasX, canvasY);

    // Pozycja okna — tylko gdy nie jest ręcznie przypięte przez użytkownika
    if (this.zoomManuallyPositioned) return;

    const rectTop = event.clientY - event.offsetY;
    const rectBot = rectTop + canvas.offsetHeight;
    const W = this.zoomW;
    const H = this.zoomH;
    let left = Math.round(event.clientX - W / 2);
    let top = Math.round(rectTop - H - 6);
    left = Math.max(8, Math.min(window.innerWidth - W - 8, left));
    top = top < 8 ? Math.round(rectBot + 6) : top;

    this.zoomCurrentLeft = left;
    this.zoomCurrentTop = top;

    const lensEl = this.zoomLensEl;
    if (lensEl) {
      lensEl.style.left = `${left}px`;
      lensEl.style.top = `${top}px`;
    }
    // Synchronizacja z Angular bindings — bez tego Angular CD nadpisuje pozycję
    // wartością sprzed ruchu myszki przy każdym cyklu detekcji zmian.
    this.ctx.setZoomGeometry(left, top, W, H);
  }

  // Local mirror of zoom dimensions (set in toggleZoom)
  private zoomW = 480;
  private zoomH = 256;

  // ── Private: zoom render ──────────────────────────────────────────────────

  private scheduleZoomRender(canvasX: number, canvasY: number): void {
    this.minimapZoomPendingCanvasX = canvasX;
    this.minimapZoomPendingCanvasY = canvasY;
    if (this.minimapZoomRenderScheduled) return;
    this.minimapZoomRenderScheduled = true;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.minimapZoomRenderScheduled = false;
        this.renderZoom(this.minimapZoomPendingCanvasX, this.minimapZoomPendingCanvasY);
      });
    });
  }

  /** Call this from component when the *ngIf zoom canvas becomes available. */
  setZoomCanvas(canvas: HTMLCanvasElement | null): void {
    this.zoomCanvas = canvas;
  }

  /**
   * Call this from component when the *ngIf zoom lens element becomes available.
   * Needed because the zoom lens (behind *ngIf="showMinimapZoom") lives in a
   * structurally separate part of the template — not a descendant of this
   * directive's host — so it can't just be resolved via querySelector in ngOnInit.
   */
  setZoomLensEl(el: HTMLDivElement | null): void {
    this.zoomLensEl = el;
  }

  /** Call this once from the component — crosshair canvas is a stable sibling element. */
  setCrosshairCanvas(canvas: HTMLCanvasElement | null): void {
    this.crosshairCanvas = canvas;
  }

  private renderZoom(canvasX: number, canvasY: number): void {
    const zoomCanvas = this.zoomCanvas ?? (this.zoomLensEl?.querySelector('canvas') );
    if (!zoomCanvas) return;
    const ctx2d = zoomCanvas.getContext('2d');
    if (!ctx2d) return;

    const W = this.zoomW;
    const H = this.zoomH;
    zoomCanvas.width = W;
    zoomCanvas.height = H;

    const SCALE = MINIMAP_ZOOM_SCALE;

    const tableClickX = (canvasX + this.minimapPanOffset) / (this.minimapScale || 1);
    const halfContentW = W / (2 * SCALE);
    const startTableX = tableClickX - halfContentW;
    const endTableX = startTableX + W / SCALE;

    const sc = this.ctx.getScrollContainer();
    const tableHost = this.ctx.getTableHost();
    if (!sc || !tableHost) return;

    const realRowH = this.ctx.getNavRowHeight();
    if (realRowH <= 0) return;

    const scaledRowH = realRowH * SCALE;

    const headerRow = tableHost.querySelector('thead tr:last-child');
    const allBodyRows = Array.from(tableHost.querySelectorAll('tbody tr')) ;
    const expectedCellCount = this.ctx.getDisplayedColumns().length;

    const maxBodySlots = Math.max(1, Math.ceil(sc.clientHeight / realRowH));
    const vpRect = sc.getBoundingClientRect();
    const allVisibleBodyRows: (Element | null)[] = [];
    for (const row of allBodyRows) {
      if (row.querySelectorAll('td, th').length !== expectedCellCount) continue;
      const r = row.getBoundingClientRect();
      if (r.bottom > vpRect.top && r.top < vpRect.bottom) allVisibleBodyRows.push(row);
    }
    while (allVisibleBodyRows.length < maxBodySlots) allVisibleBodyRows.push(null);

    const allMiniRows: (Element | null)[] = [];
    if (headerRow) allMiniRows.push(headerRow);
    allMiniRows.push(...allVisibleBodyRows.slice(0, maxBodySlots));

    const totalMiniSlots = Math.max(1, allMiniRows.length);
    const miniRowH = this.ctx.getMinimapHeight() / totalMiniSlots;
    const cursorSlotFrac = canvasY / miniRowH;

    const hasHeader = !!headerRow;
    const headerH = hasHeader ? scaledRowH : 0;

    const bodyAreaH = H - headerH;
    const totalBodySlotsInZoom = Math.max(1, Math.round(bodyAreaH / scaledRowH));

    const bodyAllRows: (Element | null)[] = hasHeader ? allMiniRows.slice(1) : [...allMiniRows];
    const cursorBodySlotFrac = Math.max(0, cursorSlotFrac - (hasHeader ? 1 : 0));
    const maxBodyStart = Math.max(0, bodyAllRows.length - totalBodySlotsInZoom);
    let startBodySlotFrac = cursorBodySlotFrac - totalBodySlotsInZoom / 2;
    startBodySlotFrac = Math.max(0, Math.min(startBodySlotFrac, maxBodyStart));
    const startBodySlot = Math.floor(startBodySlotFrac);
    const subRowOffset = (startBodySlotFrac - startBodySlot) * scaledRowH;
    const bodyRowsToRender = bodyAllRows.slice(startBodySlot, startBodySlot + totalBodySlotsInZoom + 1);

    const cursorYInZoom = (hasHeader && cursorSlotFrac < 1)
      ? cursorSlotFrac * scaledRowH
      : headerH + (cursorBodySlotFrac - startBodySlotFrac) * scaledRowH;

    const headerCount = hasHeader ? 1 : 0;
    this.minimapZoomIndicatorRect = {
      x1: startTableX * this.minimapScale - this.minimapPanOffset,
      x2: endTableX * this.minimapScale - this.minimapPanOffset,
      y1: (headerCount + startBodySlotFrac) * miniRowH,
      y2: (headerCount + startBodySlotFrac + totalBodySlotsInZoom) * miniRowH
    };

    const colOffsets: number[] = [];
    const colWidths: number[] = [];
    if (headerRow) {
      const ths = Array.from(headerRow.querySelectorAll('th'));
      let cum = 0;
      for (const th of ths) {
        colOffsets.push(cum);
        const w = (th as HTMLElement).offsetWidth || this.ctx.getInitialColumnWidth('');
        colWidths.push(w);
        cum += w;
      }
    } else {
      let cum = 0;
      for (const col of this.ctx.getDisplayedColumns()) {
        colOffsets.push(cum);
        const w = this.ctx.getInitialColumnWidth(col.name);
        colWidths.push(w);
        cum += w;
      }
    }

    ctx2d.fillStyle = getComputedStyle(document.body).getPropertyValue('--surface-ground') || '#f8f9fa';
    ctx2d.fillRect(0, 0, W, H);
    ctx2d.save();
    ctx2d.beginPath(); ctx2d.rect(0, 0, W, H); ctx2d.clip();
    ctx2d.textBaseline = 'middle';

    interface BadgeData { x: number; y: number; w: number; h: number; bg: string; text: string }
    interface CellData {
      x: number; y: number; w: number; h: number;
      bg: string; text: string; textColor: string;
      fontFamily: string; fontSize: number; fontWeight: string;
      hasSvgIcon: boolean; badges: BadgeData[];
    }
    const items: CellData[] = [];

    const collectRow = (row: Element | null, rowCanvasY: number): void => {
      if (!row) return;
      const rowStyle = getComputedStyle(row);
      const rowBg = this.resolveElementColor(rowStyle, row, true);
      const y = Math.floor(rowCanvasY);
      const h = Math.ceil(scaledRowH);
      const rowRect = (row as HTMLElement).getBoundingClientRect();
      const cells = row.querySelectorAll('td, th');

      cells.forEach((cell, colIdx) => {
        const tableX = colOffsets[colIdx] ?? 0;
        const tableW = colWidths[colIdx] ?? 0;
        if (tableX + tableW <= startTableX || tableX >= endTableX) return;

        const cellCanvasX = (tableX - startTableX) * SCALE;
        const cellCanvasW = tableW * SCALE;
        const cellRect = (cell as HTMLElement).getBoundingClientRect();
        const cellStyle = getComputedStyle(cell);
        let bg = this.resolveCellColor(cellStyle, cell, true);
        if (!bg) bg = rowBg;

        const violationEls = cell.querySelectorAll('.violation');
        const badges: BadgeData[] = [];
        violationEls.forEach(v => {
          const vEl = v as HTMLElement;
          const vRect = vEl.getBoundingClientRect();
          if (vRect.width === 0 || vRect.height === 0) return;
          const vBg = getComputedStyle(v).backgroundColor;
          if (!vBg || vBg === 'rgba(0, 0, 0, 0)' || vBg === 'transparent') return;
          const noCopyEl = (v.querySelector('.no-copy') ?? v) as HTMLElement;
          const symbol = stripHtml(noCopyEl.innerHTML?.trim() ?? noCopyEl.textContent?.trim() ?? '');
          const bX = cellCanvasX + (vRect.left - cellRect.left) * SCALE;
          const bY = y + (vRect.top - rowRect.top) * SCALE;
          badges.push({
            x: Math.floor(bX), y: Math.floor(bY),
            w: Math.ceil(vRect.width * SCALE), h: Math.ceil(vRect.height * SCALE),
            bg: vBg, text: symbol
          });
        });

        const text = (cell.textContent ?? '').trim();
        const hasSvgIcon = badges.length === 0 && !text && !!cell.querySelector('svg');
        items.push({
          x: Math.floor(cellCanvasX), y, w: Math.ceil(cellCanvasW), h, bg,
          text: badges.length > 0 ? '' : text,
          textColor: cellStyle.color, fontFamily: cellStyle.fontFamily,
          fontSize: (parseFloat(cellStyle.fontSize) || 14) * SCALE,
          fontWeight: cellStyle.fontWeight || 'normal',
          hasSvgIcon, badges
        });
      });
    };

    bodyRowsToRender.forEach((row, bodyIdx) => {
      collectRow(row, headerH + bodyIdx * scaledRowH - subRowOffset);
    });

    const headerItemsStart = items.length;
    if (hasHeader) collectRow(headerRow, 0);

    for (const { x, y, w, h, bg } of items) {
      if (bg) { ctx2d.fillStyle = bg; ctx2d.fillRect(x, y, w, h); }
    }

    const drawCellContent = (
      x: number, y: number, w: number, h: number,
      text: string, textColor: string, fontFamily: string,
      fontSize: number, fontWeight: string,
      hasSvgIcon: boolean, badges: BadgeData[]
    ): void => {
      if (badges.length > 0) {
        for (const badge of badges) {
          ctx2d.fillStyle = badge.bg;
          ctx2d.fillRect(badge.x, badge.y, badge.w, badge.h);
          if (badge.text && badge.w >= 6 && badge.h >= 4) {
            let bFontSize = Math.min(badge.h - 2, 13);
            ctx2d.font = `bold ${bFontSize}px sans-serif`;
            const measuredW = ctx2d.measureText(badge.text).width;
            const availW = badge.w - 4;
            if (measuredW > availW && availW > 0) {
              bFontSize = Math.max(5, bFontSize * availW / measuredW);
              ctx2d.font = `bold ${bFontSize}px sans-serif`;
            }
            ctx2d.fillStyle = 'rgba(255,255,255,0.9)';
            ctx2d.save();
            ctx2d.beginPath(); ctx2d.rect(badge.x - 2, badge.y, badge.w + 4, badge.h); ctx2d.clip();
            ctx2d.fillText(badge.text, badge.x + 2, badge.y + badge.h / 2);
            ctx2d.restore();
          }
        }
        return;
      }
      if (hasSvgIcon) {
        ctx2d.fillStyle = textColor || 'rgba(0,0,0,0.45)';
        const s = Math.max(4, Math.min(w * 0.5, h * 0.6, 20));
        ctx2d.fillRect(x + (w - s) / 2, y + (h - s) / 2, s, s);
        return;
      }
      if (!text) return;
      ctx2d.font = `${fontWeight} ${fontSize}px ${fontFamily || 'sans-serif'}`;
      ctx2d.fillStyle = textColor || 'rgba(0,0,0,0.8)';
      ctx2d.save();
      ctx2d.beginPath(); ctx2d.rect(x + 2, y, Math.max(0, w - 4), h); ctx2d.clip();
      ctx2d.fillText(text, x + 4, y + h / 2);
      ctx2d.restore();
    };

    ctx2d.save();
    if (hasHeader) { ctx2d.beginPath(); ctx2d.rect(0, Math.ceil(headerH), W, H); ctx2d.clip(); }
    for (let i = 0; i < headerItemsStart; i++) {
      const { x, y, w, h, text, textColor, fontFamily, fontSize, fontWeight, hasSvgIcon, badges } = items[i];
      drawCellContent(x, y, w, h, text, textColor, fontFamily, fontSize, fontWeight, hasSvgIcon, badges);
    }
    ctx2d.restore();

    if (hasHeader) {
      const surfaceBg = getComputedStyle(document.body).getPropertyValue('--surface-ground') || '#f8f9fa';
      ctx2d.fillStyle = surfaceBg;
      ctx2d.fillRect(0, 0, W, Math.ceil(scaledRowH));
      for (let i = headerItemsStart; i < items.length; i++) {
        const { x, y, w, h, bg } = items[i];
        if (bg) { ctx2d.fillStyle = bg; ctx2d.fillRect(x, y, w, h); }
      }
      for (let i = headerItemsStart; i < items.length; i++) {
        const { x, y, w, h, text, textColor, fontFamily, fontSize, fontWeight, hasSvgIcon, badges } = items[i];
        drawCellContent(x, y, w, h, text, textColor, fontFamily, fontSize, fontWeight, hasSvgIcon, badges);
      }
    }

    ctx2d.fillStyle = 'rgba(0, 0, 0, 0.08)';
    for (let i = 1; i < bodyRowsToRender.length; i++) {
      const sepY = Math.floor(headerH + i * scaledRowH - subRowOffset);
      if (sepY > headerH && sepY < H) ctx2d.fillRect(0, sepY, W, 1);
    }
    if (hasHeader) {
      ctx2d.fillStyle = 'rgba(0, 0, 0, 0.22)';
      ctx2d.fillRect(0, Math.round(scaledRowH), W, 1);
    }

    ctx2d.fillStyle = 'rgba(0, 0, 0, 0.06)';
    for (let i = 1; i < colOffsets.length; i++) {
      const sepX = Math.floor((colOffsets[i] - startTableX) * SCALE);
      if (sepX > 0 && sepX < W) ctx2d.fillRect(sepX, 0, 1, H);
    }

    const cx = Math.round(W / 2) + 0.5;
    const cy = Math.round(cursorYInZoom) + 0.5;
    ctx2d.setLineDash([]);

    ctx2d.lineWidth = 3; ctx2d.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx2d.beginPath(); ctx2d.moveTo(cx, 0); ctx2d.lineTo(cx, H); ctx2d.stroke();
    ctx2d.lineWidth = 1.5; ctx2d.strokeStyle = 'rgba(60,130,255,0.85)';
    ctx2d.beginPath(); ctx2d.moveTo(cx, 0); ctx2d.lineTo(cx, H); ctx2d.stroke();

    ctx2d.lineWidth = 3; ctx2d.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx2d.beginPath(); ctx2d.moveTo(0, cy); ctx2d.lineTo(W, cy); ctx2d.stroke();
    ctx2d.lineWidth = 1.5; ctx2d.strokeStyle = 'rgba(60,130,255,0.85)';
    ctx2d.beginPath(); ctx2d.moveTo(0, cy); ctx2d.lineTo(W, cy); ctx2d.stroke();

    ctx2d.restore();

    this.renderTableCrosshair(cursorSlotFrac, tableClickX, allMiniRows, realRowH);
    this.refreshMinimapWithIndicator();
  }

  // ── Private: crosshair ────────────────────────────────────────────────────

  private renderTableCrosshair(
    cursorSlotFrac: number,
    tableClickX: number,
    allMiniRows: (Element | null)[],
    realRowH: number
  ): void {
    const canvas = this.crosshairCanvas;
    const sc = this.ctx.getScrollContainer();
    if (!canvas || !sc) return;

    const scRect = sc.getBoundingClientRect();
    const W = Math.round(scRect.width);
    const H = Math.round(scRect.height);
    if (W <= 0 || H <= 0) return;

    canvas.style.left = `${Math.round(scRect.left)}px`;
    canvas.style.top = `${Math.round(scRect.top)}px`;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    canvas.style.display = 'block';

    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;

    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;
    ctx2d.clearRect(0, 0, W, H);
    ctx2d.setLineDash([]);

    const originLeft = scRect.left + (sc.clientLeft ?? 0);
    const originTop = scRect.top + (sc.clientTop ?? 0);

    const drawHalo = (x1: number, y1: number, x2: number, y2: number): void => {
      ctx2d.lineWidth = 3; ctx2d.strokeStyle = 'rgba(255,255,255,0.7)';
      ctx2d.beginPath(); ctx2d.moveTo(x1, y1); ctx2d.lineTo(x2, y2); ctx2d.stroke();
      ctx2d.lineWidth = 1.5; ctx2d.strokeStyle = 'rgba(60,130,255,0.85)';
      ctx2d.beginPath(); ctx2d.moveTo(x1, y1); ctx2d.lineTo(x2, y2); ctx2d.stroke();
    };

    const slotIdx = Math.floor(cursorSlotFrac);
    const fraction = cursorSlotFrac - slotIdx;
    const rowEl = slotIdx < allMiniRows.length ? allMiniRows[slotIdx] : null;
    if (rowEl) {
      const rowRect = (rowEl as HTMLElement).getBoundingClientRect();
      const lineY = rowRect.top - originTop + fraction * realRowH;
      if (lineY >= 0 && lineY <= H) drawHalo(0, Math.round(lineY) + 0.5, W, Math.round(lineY) + 0.5);
    }

    const firstRowEl = allMiniRows[0];
    if (firstRowEl) {
      const firstTh = firstRowEl.querySelector('th') as HTMLElement | null;
      if (firstTh) {
        const firstThRect = firstTh.getBoundingClientRect();
        const lineX = firstThRect.left + tableClickX - originLeft;
        if (lineX >= 0 && lineX <= W) drawHalo(Math.round(lineX) + 0.5, 0, Math.round(lineX) + 0.5, H);
      }
    }
  }

  // ── Private: color resolution ─────────────────────────────────────────────

  private resolveElementColor(style: CSSStyleDeclaration, el?: Element, includeHover = false): string {
    const bg = style.backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      const isHovered = !!el?.matches?.(':hover');
      const isAnimating = style.animationName !== 'none';
      const isSelected = !!el?.matches?.('.p-highlight, [aria-selected="true"]');
      if (((!isHovered && !isAnimating) || isSelected || includeHover)) {
        return toMinimapColor(bg);
      }
    }
    return resolveColorFromVarChain(style) || resolveGradientColor(style.backgroundImage);
  }

  private resolveCellColor(style: CSSStyleDeclaration, el?: Element, includeHover = false): string {
    const bg = style.backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      const row = el?.closest('tr');
      const isHovered = !!row?.matches?.(':hover');
      const isAnimating = style.animationName !== 'none';
      const isSelected = !!row?.matches?.('.p-highlight, [aria-selected="true"]');
      if (includeHover && isHovered && !isSelected) {
        return toMinimapColor(bg);
      }
      if (!isSurfaceLike(bg) && ((!isHovered && !isAnimating) || isSelected)) {
        return toMinimapColor(bg);
      }
    }
    return resolveColorFromVarChain(style) || resolveGradientColor(style.backgroundImage);
  }
}
